/**
 * bridge/secure_storage_controller.js — "secure-storage" bridge component
 * (Stimulus + Strada).
 *
 * Hardware-backed key/value storage: the iOS Keychain and Android's
 * EncryptedSharedPreferences. Stimulus identifier "bridge--secure-storage"
 * (glob-derived from this subdir); Strada component name "secure-storage" MUST
 * match the iOS class var name
 * (native/ios/Chippie/Bridge/SecureStorageComponent.swift).
 *
 * Why it exists: everything the web can reach — localStorage, sessionStorage,
 * IndexedDB, cookies — is plaintext on disk inside the app sandbox, survives
 * nothing, and is readable by anyone with the device unlocked or a backup of it.
 * That is fine for UI preferences and wrong for the two things offline mode
 * needs to hold: the PowerSync sync credential and whatever token keeps a tradie
 * signed in for weeks between opens. It is also what makes bridge--biometrics-lock
 * mean anything: a Face ID gate in front of a secret that sits in plaintext next
 * to it is theatre.
 *
 * API — window.ChippieBridge.secureStorage (all Promise-based, because the
 * Keychain round trip is asynchronous):
 *   .set(key, value) -> Promise<Boolean>        value is coerced to a string
 *   .get(key)        -> Promise<String | null>  null when the key is absent
 *   .remove(key)     -> Promise<Boolean>
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection):
 *   - ios:     this.send("set" | "get" | "remove", { key, value }, cb) ->
 *              kSecClassGenericPassword items scoped to the app's bundle id.
 *   - android: window.ChippieNative.secureSet/secureGet/secureRemove — synchronous
 *              across the binder, wrapped in a resolved Promise so callers write
 *              one code path.
 *   - pwa:     REJECTS with Error("secure-storage-unavailable"). There is no
 *              secure storage in a browser, and quietly falling back to
 *              localStorage would put a credential the caller believes is in the
 *              Keychain into plaintext — a silent security downgrade is worse
 *              than a loud unavailability. Callers keep credentials in the
 *              session cookie on the web, exactly as they do today.
 *
 * NOT server-drivable: absent from REMOTE_ALLOWED on purpose. These are
 * credentials; a server-pushed write or read of the device keystore is not a
 * capability this app wants to own, whatever the payload claims to be for.
 *
 * Never log a key's VALUE. If a value ever needs to travel through Python, the
 * field name goes in SENSITIVE_KEYS (application/utils/log_redaction.py) first.
 *
 * Template wiring (one global instance in the shell is enough):
 *   <div data-controller="bridge--secure-storage"></div>
 *
 * Outside a native shell BridgeComponent never connect()s, so the Android/PWA
 * impls are wired standalone at module load and connect() upgrades iOS.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

const UNAVAILABLE = "secure-storage-unavailable";

function androidImpl() {
    const native = window.ChippieNative;
    const missing = () => Promise.reject(new Error(UNAVAILABLE));
    return {
        set: (key, value) => {
            if (!native || !native.secureSet) return missing();
            try {
                return Promise.resolve(!!native.secureSet(String(key), String(value)));
            } catch (e) {
                return Promise.reject(e);
            }
        },
        get: (key) => {
            if (!native || !native.secureGet) return missing();
            try {
                const value = native.secureGet(String(key));
                // The binder marshals a Kotlin null to JS null; normalise
                // undefined (older WebView builds) to the same absent value.
                return Promise.resolve(value === undefined ? null : value);
            } catch (e) {
                return Promise.reject(e);
            }
        },
        remove: (key) => {
            if (!native || !native.secureRemove) return missing();
            try {
                return Promise.resolve(!!native.secureRemove(String(key)));
            } catch (e) {
                return Promise.reject(e);
            }
        },
    };
}

// A browser has nowhere secure to put this. Reject rather than degrade.
const pwaImpl = {
    set: () => Promise.reject(new Error(UNAVAILABLE)),
    get: () => Promise.reject(new Error(UNAVAILABLE)),
    remove: () => Promise.reject(new Error(UNAVAILABLE)),
};

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("secureStorage", {
        // ios is overridden by connect() with real sends; the placeholder
        // rejects so a pre-connect call fails loudly instead of resolving null.
        ios: pwaImpl,
        android: androidImpl(),
        pwa: pwaImpl,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "secure-storage";

    connect() {
        super.connect();
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("secureStorage", {
            ios: {
                set: (key, value) => this.request("set", { key: String(key), value: String(value) })
                    .then((data) => !!data.success),
                get: (key) => this.request("get", { key: String(key) })
                    .then((data) => (data.value === undefined ? null : data.value)),
                remove: (key) => this.request("remove", { key: String(key) })
                    .then((data) => !!data.success),
            },
            android: androidImpl(),
            pwa: pwaImpl,
        });
    }

    disconnect() {
        registerStandalone();
        super.disconnect();
    }

    // Strada replies through a callback; every method here is Promise-shaped, so
    // adapt once rather than three times.
    request(event, payload) {
        return new Promise((resolve) => {
            this.send(event, payload, (message) => resolve((message && message.data) || {}));
        });
    }
}
